import type { SatzgliedRole, SatzgliederSourceChunk, SatzgliederSourceSentence, SatzgliederVariant } from "./satzglieder-saetze";

type Case = "nominative" | "dative" | "accusative";
type Gender = "masculine" | "feminine" | "neuter";
type ObjectPool = "reading" | "school" | "school-small" | "person";

interface Phrase { text: string; categories?: Array<Exclude<ObjectPool, "person">> }
interface PersonBase { noun: string; gender: Gender }
interface ThingBase extends PersonBase { categories: Array<Exclude<ObjectPool, "person">> }
interface SplitPredicate { front: string; back: string; objectPool: ObjectPool; needsDative: boolean }
interface PrepositionalPredicate { predicate: string; preposition: string; objectPool: "person-accusative" | "person-dative" | "thing-accusative" }

export type SatzgliederGeneratorFamily =
  | "svo"
  | "ovs"
  | "split-perfect"
  | "split-separable"
  | "adv-fronted"
  | "adv-middle"
  | "adv-end"
  | "prep-object"
  | "prep-fronted"
  | "prep-adverbial";

const PERSON_BASES: PersonBase[] = [
  { noun: "Trainer", gender: "masculine" }, { noun: "Freundin", gender: "feminine" },
  { noun: "Opa", gender: "masculine" }, { noun: "Tante", gender: "feminine" },
  { noun: "Kind", gender: "neuter" }, { noun: "Arzt", gender: "masculine" },
  { noun: "Ärztin", gender: "feminine" }, { noun: "Onkel", gender: "masculine" },
  { noun: "Köchin", gender: "feminine" }, { noun: "Nachbarin", gender: "feminine" },
];

const THING_BASES: ThingBase[] = [
  { noun: "Ring", gender: "masculine", categories: ["school-small", "school"] },
  { noun: "Becher", gender: "masculine", categories: ["school-small", "school"] },
  { noun: "Zettel", gender: "masculine", categories: ["reading", "school-small", "school"] },
  { noun: "Plan", gender: "masculine", categories: ["reading", "school"] },
  { noun: "Koffer", gender: "masculine", categories: ["school"] },
  { noun: "Karte", gender: "feminine", categories: ["reading", "school-small", "school"] },
  { noun: "Flasche", gender: "feminine", categories: ["school-small", "school"] },
  { noun: "Liste", gender: "feminine", categories: ["reading", "school"] },
  { noun: "Poster", gender: "neuter", categories: ["reading", "school"] },
  { noun: "Buch", gender: "neuter", categories: ["reading", "school"] },
];

const PERSON_ADJECTIVES = ["fröhlich", "neugierig", "pünktlich", "sportlich", "gründlich"];
const THING_ADJECTIVES = ["blau", "leicht", "neu", "glatt", "klar"];

function upperFirst(text: string) { return text.charAt(0).toUpperCase() + text.slice(1); }
function lowerFirst(text: string) { return text.charAt(0).toLowerCase() + text.slice(1); }

function inflectedPhrase(base: PersonBase, adjectiveStem: string, grammaticalCase: Case, categories?: ThingBase["categories"]): Phrase {
  const articles: Record<Case, Record<Gender, string>> = {
    nominative: { masculine: "der", feminine: "die", neuter: "das" },
    dative: { masculine: "dem", feminine: "der", neuter: "dem" },
    accusative: { masculine: "den", feminine: "die", neuter: "das" },
  };
  const ending = grammaticalCase === "dative" || (grammaticalCase === "accusative" && base.gender === "masculine") ? "en" : "e";
  return { text: `${upperFirst(articles[grammaticalCase][base.gender])} ${adjectiveStem}${ending} ${base.noun}`, ...(categories ? { categories: [...categories] } : {}) };
}

function fillUnique(target: Phrase[], candidates: Phrase[], length: number) {
  const seen = new Set(target.map(({ text }) => text));
  for (const candidate of candidates) {
    if (seen.has(candidate.text)) continue;
    target.push(candidate);
    seen.add(candidate.text);
    if (target.length >= length) break;
  }
  return target;
}

const SUBJECTS = fillUnique([
  "Der freundliche Lehrer", "Die kluge Schülerin", "Der ruhige Schüler", "Das mutige Mädchen", "Der neue Junge",
  "Die nette Mutter", "Der geduldige Vater", "Die fleißige Lehrerin", "Der hilfsbereite Nachbar", "Die aufmerksame Schwester",
].map((text) => ({ text })), PERSON_BASES.flatMap((base) => PERSON_ADJECTIVES.map((adjective) => inflectedPhrase(base, adjective, "nominative"))), 50);

const DATIVE_OBJECTS = fillUnique([
  "Dem kleinen Bruder", "Dem neuen Lehrer", "Der netten Nachbarin", "Dem ruhigen Schüler",
  "Der freundlichen Mutter", "Dem alten Opa", "Der jungen Schwester", "Dem hilfsbereiten Trainer",
].map((text) => ({ text })), PERSON_BASES.flatMap((base) => PERSON_ADJECTIVES.map((adjective) => inflectedPhrase(base, adjective, "dative"))), 50);

const ACCUSATIVE_PERSON_OBJECTS = fillUnique([
  "Den neuen Lehrer", "Den ruhigen Schüler", "Den hilfsbereiten Nachbarn", "Die kluge Schülerin", "Die junge Schwester", "Die nette Mutter",
].map((text) => ({ text })), PERSON_BASES.flatMap((base) => PERSON_ADJECTIVES.map((adjective) => inflectedPhrase(base, adjective, "accusative"))), 50);

const ACCUSATIVE_OBJECTS = fillUnique([
  { text: "Den roten Ball", categories: ["school-small", "school"] },
  { text: "Den gelben Stift", categories: ["school-small", "school"] },
  { text: "Den langen Brief", categories: ["reading", "school"] },
  { text: "Den schweren Ranzen", categories: ["school"] },
  { text: "Die blaue Mappe", categories: ["school-small", "school"] },
  { text: "Die leichte Aufgabe", categories: ["reading", "school"] },
  { text: "Das bunte Bild", categories: ["reading", "school"] },
  { text: "Das neue Heft", categories: ["reading", "school-small", "school"] },
] satisfies Phrase[], THING_BASES.flatMap((base) => THING_ADJECTIVES.map((adjective) => inflectedPhrase(base, adjective, "accusative", base.categories))), 50);

const PREDICATES = [
  "zeigt", "bringt", "gibt", "reicht", "schickt", "leiht", "überreicht", "erklärt", "verkauft", "schenkt",
  "verspricht", "empfiehlt", "beschreibt", "erzählt", "nennt", "meldet", "sendet", "liefert", "präsentiert", "beantwortet",
  "verrät", "bietet", "besorgt", "bestellt", "schreibt", "überlässt", "übergibt", "borgt", "spendet", "widmet",
  "übermittelt", "übersendet", "verleiht", "vermittelt", "demonstriert", "zusendet", "zuschickt", "aushändigt", "überbringt", "serviert",
  "diktiert", "reserviert", "hinterlässt", "spendiert", "versendet", "beweist", "verschafft", "notiert", "genehmigt", "stellt",
];

const SPLIT_PERFECT: SplitPredicate[] = [
  { front: "hat", back: "gelesen", objectPool: "reading", needsDative: false },
  { front: "hat", back: "geschrieben", objectPool: "reading", needsDative: false },
  { front: "hat", back: "gebracht", objectPool: "school", needsDative: true },
  { front: "hat", back: "gezeigt", objectPool: "school", needsDative: true },
  { front: "hat", back: "geliehen", objectPool: "school", needsDative: true },
];

const SPLIT_SEPARABLE: SplitPredicate[] = [
  { front: "packt", back: "aus", objectPool: "school", needsDative: false },
  { front: "hebt", back: "auf", objectPool: "school-small", needsDative: false },
  { front: "liest", back: "vor", objectPool: "reading", needsDative: true },
  { front: "ruft", back: "an", objectPool: "person", needsDative: false },
  { front: "schreit", back: "an", objectPool: "person", needsDative: false },
];

const ADVERBIALS = ["Heute", "Morgen", "Nach der Pause", "Im Klassenzimmer", "Im Garten", "Vor der Schule", "Ganz leise", "Mit großer Freude"];

const PREPOSITIONAL_PREDICATES: PrepositionalPredicate[] = [
  { predicate: "wartet", preposition: "auf", objectPool: "person-accusative" },
  { predicate: "denkt", preposition: "an", objectPool: "person-accusative" },
  { predicate: "spricht", preposition: "über", objectPool: "thing-accusative" },
  { predicate: "bittet", preposition: "um", objectPool: "thing-accusative" },
  { predicate: "rechnet", preposition: "mit", objectPool: "person-dative" },
  { predicate: "träumt", preposition: "von", objectPool: "person-dative" },
  { predicate: "erzählt", preposition: "von", objectPool: "person-dative" },
  { predicate: "fragt", preposition: "nach", objectPool: "person-dative" },
];

function accusatives(pool: ObjectPool) {
  if (pool === "person") return ACCUSATIVE_PERSON_OBJECTS;
  return ACCUSATIVE_OBJECTS.filter(({ categories }) => categories?.includes(pool));
}

function splitCount(predicate: SplitPredicate) {
  return SUBJECTS.length * (predicate.needsDative ? DATIVE_OBJECTS.length : 1) * accusatives(predicate.objectPool).length;
}

const CANONICAL_COUNT = SUBJECTS.length * PREDICATES.length * DATIVE_OBJECTS.length * ACCUSATIVE_OBJECTS.length;
const SPLIT_PERFECT_COUNT = SPLIT_PERFECT.reduce((sum, predicate) => sum + splitCount(predicate), 0);
const SPLIT_SEPARABLE_COUNT = SPLIT_SEPARABLE.reduce((sum, predicate) => sum + splitCount(predicate), 0);
const PREPOSITIONAL_COUNT = PREPOSITIONAL_PREDICATES.length * SUBJECTS.length * ACCUSATIVE_PERSON_OBJECTS.length;

export const SATZGLIEDER_GENERATOR_POOL_COUNTS: Readonly<Record<SatzgliederGeneratorFamily, number>> = {
  svo: CANONICAL_COUNT,
  ovs: CANONICAL_COUNT,
  "split-perfect": SPLIT_PERFECT_COUNT,
  "split-separable": SPLIT_SEPARABLE_COUNT,
  "adv-fronted": CANONICAL_COUNT * ADVERBIALS.length,
  "adv-middle": CANONICAL_COUNT * ADVERBIALS.length,
  "adv-end": CANONICAL_COUNT * ADVERBIALS.length,
  "prep-object": PREPOSITIONAL_COUNT,
  "prep-fronted": PREPOSITIONAL_COUNT,
  "prep-adverbial": PREPOSITIONAL_COUNT * ADVERBIALS.length,
};

const BASE_FAMILIES: SatzgliederGeneratorFamily[] = ["svo", "ovs", "split-perfect", "split-separable"];
const ADVERBIAL_FAMILIES: SatzgliederGeneratorFamily[] = ["adv-fronted", "adv-middle", "adv-end"];
const PREPOSITIONAL_FAMILIES: SatzgliederGeneratorFamily[] = ["prep-object", "prep-fronted"];

export const SATZGLIEDER_GENERATOR_TOTALS = {
  basis: BASE_FAMILIES.reduce((sum, family) => sum + SATZGLIEDER_GENERATOR_POOL_COUNTS[family], 0),
  withAdverbial: [...BASE_FAMILIES, ...ADVERBIAL_FAMILIES].reduce((sum, family) => sum + SATZGLIEDER_GENERATOR_POOL_COUNTS[family], 0),
  withPrepositional: [...BASE_FAMILIES, ...PREPOSITIONAL_FAMILIES].reduce((sum, family) => sum + SATZGLIEDER_GENERATOR_POOL_COUNTS[family], 0),
  withBoth: Object.values(SATZGLIEDER_GENERATOR_POOL_COUNTS).reduce((sum, count) => sum + count, 0),
} as const;

export function satzgliederGeneratorFamilies(roles: readonly SatzgliedRole[]) {
  const enabled = new Set(roles);
  const families = [...BASE_FAMILIES];
  if (enabled.has("Adverbiale Bestimmung")) families.push(...ADVERBIAL_FAMILIES);
  if (enabled.has("Präpositionalobjekt")) families.push(...PREPOSITIONAL_FAMILIES);
  if (enabled.has("Adverbiale Bestimmung") && enabled.has("Präpositionalobjekt")) families.push("prep-adverbial");
  return families;
}

function indices(ordinal: number, sizes: number[]) {
  let remaining = ordinal;
  return sizes.map((size) => {
    const value = remaining % size;
    remaining = Math.floor(remaining / size);
    return value;
  });
}

function chunk(id: string, text: string, expectedRole: SatzgliedRole, caseSignal?: SatzgliederSourceChunk["caseSignal"], predicatePart?: SatzgliederSourceChunk["predicatePart"]): SatzgliederSourceChunk {
  return { id, text, expectedRole, ...(caseSignal ? { caseSignal } : {}), ...(predicatePart ? { predicatePart } : {}) };
}

function sentence(id: string, variant: SatzgliederVariant, chunks: SatzgliederSourceChunk[], frontedRole?: SatzgliedRole): SatzgliederSourceSentence {
  return {
    id,
    variant,
    ...(frontedRole ? { frontedRole } : {}),
    chunks: chunks.map((entry, index) => ({ ...entry, text: index === 0 ? upperFirst(entry.text) : lowerFirst(entry.text) })),
    suffix: ".",
  };
}

function canonicalParts(ordinal: number) {
  const [subjectIndex, predicateIndex, dativeIndex, accusativeIndex] = indices(ordinal, [SUBJECTS.length, PREDICATES.length, DATIVE_OBJECTS.length, ACCUSATIVE_OBJECTS.length]);
  return {
    subject: SUBJECTS[subjectIndex!]!, predicate: PREDICATES[predicateIndex!]!,
    dative: DATIVE_OBJECTS[dativeIndex!]!, accusative: ACCUSATIVE_OBJECTS[accusativeIndex!]!,
  };
}

function splitParts(predicates: SplitPredicate[], ordinal: number) {
  let offset = ordinal;
  for (const predicate of predicates) {
    const objects = accusatives(predicate.objectPool);
    const count = splitCount(predicate);
    if (offset >= count) { offset -= count; continue; }
    const sizes = predicate.needsDative ? [SUBJECTS.length, DATIVE_OBJECTS.length, objects.length] : [SUBJECTS.length, objects.length];
    const values = indices(offset, sizes);
    return predicate.needsDative
      ? { predicate, subject: SUBJECTS[values[0]!]!, dative: DATIVE_OBJECTS[values[1]!]!, accusative: objects[values[2]!]! }
      : { predicate, subject: SUBJECTS[values[0]!]!, dative: null, accusative: objects[values[1]!]! };
  }
  throw new Error("Die Satzglieder-Kombination liegt außerhalb des Generatorpools.");
}

function prepositionalParts(ordinal: number) {
  const perPredicate = SUBJECTS.length * ACCUSATIVE_PERSON_OBJECTS.length;
  const predicateIndex = Math.floor(ordinal / perPredicate);
  const [subjectIndex, objectIndex] = indices(ordinal % perPredicate, [SUBJECTS.length, ACCUSATIVE_PERSON_OBJECTS.length]);
  const predicate = PREPOSITIONAL_PREDICATES[predicateIndex!]!;
  const objects = predicate.objectPool === "thing-accusative" ? ACCUSATIVE_OBJECTS
    : predicate.objectPool === "person-dative" ? DATIVE_OBJECTS
      : ACCUSATIVE_PERSON_OBJECTS;
  return { predicate, subject: SUBJECTS[subjectIndex!]!, object: objects[objectIndex!]! };
}

function prepositionalText(predicate: PrepositionalPredicate, object: Phrase) {
  return `${predicate.preposition} ${lowerFirst(object.text)}`;
}

export function buildSatzgliederGeneratorSentence(family: SatzgliederGeneratorFamily, ordinal: number): SatzgliederSourceSentence {
  const count = SATZGLIEDER_GENERATOR_POOL_COUNTS[family];
  if (!Number.isInteger(ordinal) || ordinal < 0 || ordinal >= count) throw new Error("Ungültiger Satzglieder-Generatorindex.");
  const id = `sg-${family}-${ordinal + 1}`;
  if (family === "svo" || family === "ovs") {
    const part = canonicalParts(ordinal);
    const chunks = family === "svo"
      ? [chunk("subject", part.subject.text, "Subjekt"), chunk("predicate", part.predicate, "Prädikat"), chunk("dative-object", part.dative.text, "Dativobjekt", "dative"), chunk("accusative-object", part.accusative.text, "Akkusativobjekt", "accusative")]
      : [chunk("accusative-object", part.accusative.text, "Akkusativobjekt", "accusative"), chunk("predicate", part.predicate, "Prädikat"), chunk("subject", part.subject.text, "Subjekt"), chunk("dative-object", part.dative.text, "Dativobjekt", "dative")];
    return sentence(id, family === "svo" ? "svo_canonical" : "ovs_fronted_object", chunks, family === "svo" ? "Subjekt" : "Akkusativobjekt");
  }
  if (family === "split-perfect" || family === "split-separable") {
    const part = splitParts(family === "split-perfect" ? SPLIT_PERFECT : SPLIT_SEPARABLE, ordinal);
    const chunks = [chunk("subject", part.subject.text, "Subjekt"), chunk("predicate-front", part.predicate.front, "Prädikat", undefined, "front")];
    if (part.dative) chunks.push(chunk("dative-object", part.dative.text, "Dativobjekt", "dative"));
    chunks.push(chunk("accusative-object", part.accusative.text, "Akkusativobjekt", "accusative"));
    chunks.push(chunk("predicate-back", part.predicate.back, "Prädikat", undefined, "back"));
    return sentence(id, "split_predicate", chunks, "Subjekt");
  }
  if (family === "adv-fronted" || family === "adv-middle" || family === "adv-end") {
    const adverbialIndex = ordinal % ADVERBIALS.length;
    const part = canonicalParts(Math.floor(ordinal / ADVERBIALS.length));
    const subject = chunk("subject", part.subject.text, "Subjekt");
    const predicate = chunk("predicate", part.predicate, "Prädikat");
    const dative = chunk("dative-object", part.dative.text, "Dativobjekt", "dative");
    const accusative = chunk("accusative-object", part.accusative.text, "Akkusativobjekt", "accusative");
    const adverbial = chunk("adverbial", ADVERBIALS[adverbialIndex!]!, "Adverbiale Bestimmung");
    const chunks = family === "adv-fronted" ? [adverbial, predicate, subject, dative, accusative]
      : family === "adv-middle" ? [subject, predicate, adverbial, dative, accusative]
        : [subject, predicate, dative, accusative, adverbial];
    return sentence(id, family === "adv-fronted" ? "adv_fronted" : "svo_canonical", chunks, family === "adv-fronted" ? "Adverbiale Bestimmung" : "Subjekt");
  }
  const hasAdverbial = family === "prep-adverbial";
  const prepositionalOrdinal = hasAdverbial ? Math.floor(ordinal / ADVERBIALS.length) : ordinal;
  const part = prepositionalParts(prepositionalOrdinal);
  const subject = chunk("subject", part.subject.text, "Subjekt");
  const predicate = chunk("predicate", part.predicate.predicate, "Prädikat");
  const prepositional = chunk("prepositional-object", prepositionalText(part.predicate, part.object), "Präpositionalobjekt", "prepositional");
  if (family === "prep-fronted") return sentence(id, "prepositional_object", [prepositional, predicate, subject], "Präpositionalobjekt");
  const chunks = [subject, predicate, prepositional];
  if (hasAdverbial) chunks.push(chunk("adverbial", ADVERBIALS[ordinal % ADVERBIALS.length]!, "Adverbiale Bestimmung"));
  return sentence(id, "prepositional_object", chunks, "Subjekt");
}
